import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { ClipboardList } from 'lucide-react';
import { DataTable } from '@/components/Table';
import { Pagination, PaginationState } from '@/components';
import { useGetPatientFacesheets } from '../../../hooks/patientsAPI';
import { createColumns } from './columns';
import type { ViewFacesheetSummaryParams } from './types';

const DEFAULT_PAGE_SIZE = 10;

// Empty state when FSG is enabled but no completed analysis results
const EmptyState = () => (
  <div className="flex min-h-[250px] flex-col items-center justify-center py-12">
    <img
      src="/suggested-providers.png"
      alt="No facesheets available"
      className="mb-4 max-w-[200px]"
    />
    <h3 className="text-lg font-semibold">Facesheets will appear here.</h3>
    <p className="text-muted-foreground text-sm">
      Get started with a new patient request.
    </p>
  </div>
);

interface FacesheetAnalysisProps {
  onViewImage: (signedUrl: string) => void;
  onViewFacesheet: (params: ViewFacesheetSummaryParams) => void;
}

export const FacesheetAnalysis = ({
  onViewImage,
  onViewFacesheet,
}: FacesheetAnalysisProps) => {
  const { patientId } = useParams<{ patientId: string }>();

  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const { data, isFetching } = useGetPatientFacesheets(
    patientId
      ? {
          patientId,
          pagination: {
            offset: pagination.page * pagination.pageSize,
            limit: pagination.pageSize,
          },
        }
      : undefined,
  );

  const columns = useMemo(
    () => createColumns({ onViewImage, onViewFacesheet }),
    [onViewImage, onViewFacesheet],
  );

  const facesheets = data?.facesheets ?? [];
  const totalCount = data?.totalCount ?? 0;
  const hasNoResults = !isFetching && totalCount === 0;

  return (
    <div className="rounded-lg border bg-white p-6">
      <div className="mb-4 flex items-center gap-2">
        <ClipboardList className="size-5" />
        <h2 className="text-lg font-semibold">Analysis</h2>
      </div>
      {hasNoResults ? (
        <EmptyState />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={facesheets}
            isLoading={isFetching}
            pageSize={pagination.pageSize}
          />
          <Pagination
            pagination={pagination}
            setPagination={setPagination}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
          />
        </>
      )}
    </div>
  );
};
